// OrderstatusImage.js - UI matched with Shops Management | Pagination exactly like Addresss
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FaImage,
  FaSearch,
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheckDouble,
  FaEllipsisV,
  FaFileImage,
  FaSpinner,
  FaBuilding,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { IoMdArrowDropdown } from "react-icons/io";
import { RiDeleteBinFill } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import TablePagination from '../../components/TablePagination'; // ⭐ pagination
import './OrderstatusImage.css';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

// ⭐ Har page par sirf itni rows
const PAGE_SIZE = 10;

// Last loaded records of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const OS_CACHE_PREFIX = 'orderstatus_cache_';
const OS_CITIES_PREFIX = 'orderstatus_cities_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

// ✅ Image record ka status -> Order ka asli status (dono jagah ek hi naam)
const toOrderStatus = (s) => {
  if (s === 'Order Placed') return 'Pending';
  if (s === 'Cancelled') return 'Canceled';
  return s;
};

const normalizeOrderStatus = (s) => (s === 'In Process' ? 'Processing' : (s || ''));

const OrderstatusImage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The shop is known from the very first render (from the URL), and the saved
  // list of that shop is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const urlMatch = location.pathname.match(/\/shop\/(\d+)/);
    const startShopId = urlMatch
      ? urlMatch[1]
      : (localStorage.getItem('selectedShopId') || localStorage.getItem('selected_shop_id') || '');
    const cachedData = startShopId ? readJson(`${OS_CACHE_PREFIX}${startShopId}`) : null;
    const cachedCities = startShopId ? readJson(`${OS_CITIES_PREFIX}${startShopId}`) : null;
    initialRef.current = {
      shopId: startShopId ? String(startShopId) : '',
      data: Array.isArray(cachedData) ? cachedData : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null
    };
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  // loading = a save / delete is running (disables the buttons)
  const [loading, setLoading] = useState(false);
  // True only while there is nothing to show yet (so "No order status records found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.data);
  const [error, setError] = useState(null);

  // ⭐ Pagination state
  const [page, setPage] = useState(1);

  // Form fields
  const [selectedShopId, setSelectedShopId] = useState(initialRef.current.shopId);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [status, setStatus] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [dateUploaded, setDateUploaded] = useState('');
  const [timeUploaded, setTimeUploaded] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  // ✅ City filter for header
  const [filterCities, setFilterCities] = useState(initialRef.current.cities || []);
  const [selectedFilterCity, setSelectedFilterCity] = useState('');
  const [loadingFilterCities, setLoadingFilterCities] = useState(false);

  // Shop data
  const [shops, setShops] = useState([]);
  const [orderstatusData, setOrderstatusData] = useState(initialRef.current.data || []);
  const [allOrders, setAllOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [currentShopName, setCurrentShopName] = useState('');
  const [shopCities, setShopCities] = useState(initialRef.current.cities || []);

  const shopsRef = useRef([]);
  shopsRef.current = shops;
  const prevShopRef = useRef(initialRef.current.shopId);
  // The shop the list in `orderstatusData` belongs to (the saved copy is only written for it)
  const dataShopRef = useRef(initialRef.current.data ? initialRef.current.shopId : null);
  const imagesRequestRef = useRef(0);
  const ordersRequestRef = useRef(0);
  const citiesRequestRef = useRef(0);

  const getShopIdFromSources = useCallback(() => {
    const urlMatch = location.pathname.match(/\/shop\/(\d+)/);
    if (urlMatch) return urlMatch[1];
    const stored = localStorage.getItem('selectedShopId') || localStorage.getItem('selected_shop_id');
    if (stored) return stored;
    return null;
  }, [location.pathname]);

  const getAuthToken = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token') || localStorage.getItem('accessToken');
    if (token) return token;
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      return userData?.access_token || userData?.token || null;
    } catch (error) { return null; }
  };

  const apiFetch = useCallback(async (url, options = {}) => {
    const token = getAuthToken();
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    const headers = { ...options.headers };
    if (!(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(fullUrl, { ...options, headers });
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error('Server returned HTML page. Please check authentication or URL.');
    }
    if (!response.ok) {
      let errorMessage = `API error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData?.error || errorData?.message || errorData?.detail || errorMessage;
      } catch (e) {}
      throw new Error(errorMessage);
    }
    if (response.status === 204) return null;
    return response.json();
  }, []);

  // ✅ Orders load karna (modal ka Order Number dropdown + order ka live status)
  const loadOrders = useCallback(async (shopId) => {
    if (!shopId) return;
    const ordersRequestId = ++ordersRequestRef.current;
    try {
      const orders = await apiFetch(`/order/getAllorder/?shop_id=${shopId}`);
      if (ordersRequestId !== ordersRequestRef.current) return;
      let ordersArray = [];
      if (orders && orders.orders && Array.isArray(orders.orders)) {
        ordersArray = orders.orders;
      } else if (Array.isArray(orders)) {
        ordersArray = orders;
      }
      setAllOrders(ordersArray);
      setFilteredOrders(ordersArray);
    } catch (error) {
      if (ordersRequestId !== ordersRequestRef.current) return;
      console.error('Error fetching orders:', error);
      setError(error.message);
      setAllOrders([]);
      setFilteredOrders([]);
    }
  }, [apiFetch]);

  // Fetch shops (once)
  useEffect(() => {
    const fetchShops = async () => {
      try {
        const data = await apiFetch('/user/shops/');
        let shopsData = [];
        if (data?.results) shopsData = data.results;
        else if (Array.isArray(data)) shopsData = data;
        else if (data?.shops) shopsData = data.shops;
        setShops(shopsData);

        const shopIdFromSources = getShopIdFromSources();
        if (shopIdFromSources) {
          const shopIdStr = String(shopIdFromSources);
          setSelectedShopId(shopIdStr);
          if (!location.pathname.match(/\/shop\/\d+/)) {
            navigate(`/shop/${shopIdStr}/OrderstatusImage`, { replace: true });
          }
        } else if (shopsData.length > 0) {
          const firstShop = shopsData[0];
          const firstShopId = String(firstShop.shop_id);
          setSelectedShopId(firstShopId);
          localStorage.setItem('selectedShopId', firstShopId);
          navigate(`/shop/${firstShopId}/OrderstatusImage`, { replace: true });
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching shops:', error);
        if (!initialRef.current.shopId) setIsLoading(false);
      }
    };
    fetchShops();
  }, []);

  // Watch shop changes
  useEffect(() => {
    const shopIdFromSources = getShopIdFromSources();
    if (shopIdFromSources && String(shopIdFromSources) !== String(selectedShopId)) {
      setSelectedShopId(String(shopIdFromSources));
    }
  }, [location.pathname, selectedShopId, getShopIdFromSources]);

  // Shop name
  useEffect(() => {
    if (!selectedShopId) return;
    const shop = shops.find((s) => String(s.shop_id) === String(selectedShopId));
    setCurrentShopName(shop ? shop.shop_name : `Shop ${selectedShopId}`);
  }, [selectedShopId, shops]);

  // If the cities could not be loaded from the server, the cities of the shop are used
  useEffect(() => {
    if (!selectedShopId || shops.length === 0) return;
    const shop = shops.find((s) => String(s.shop_id) === String(selectedShopId));
    if (shop?.cities?.length) {
      setShopCities((prev) => (prev.length ? prev : shop.cities));
      setFilterCities((prev) => (prev.length ? prev : shop.cities));
    }
  }, [shops, selectedShopId]);

  // Data of the shop: show the saved list of the shop immediately, then refresh it.
  // Cities, records and orders load together instead of one after another.
  useEffect(() => {
    if (!selectedShopId) return;
    const shopId = selectedShopId;

    if (prevShopRef.current !== shopId) {
      prevShopRef.current = shopId;
      const cachedData = readJson(`${OS_CACHE_PREFIX}${shopId}`);
      const cachedCities = readJson(`${OS_CITIES_PREFIX}${shopId}`);
      if (Array.isArray(cachedData)) {
        dataShopRef.current = shopId;
        setOrderstatusData(cachedData);
        setIsLoading(false);
      } else {
        dataShopRef.current = null;
        setOrderstatusData([]);
        setIsLoading(true);
      }
      const savedCities = Array.isArray(cachedCities) ? cachedCities : [];
      setFilterCities(savedCities);
      setShopCities(savedCities);
      setSelectedFilterCity('');
      setSelectedItems([]);
      setAllOrders([]);
      setFilteredOrders([]);
      setPage(1); // ⭐ shop change → page 1
    }

    // ----- cities (header + modal) -----
    const citiesRequestId = ++citiesRequestRef.current;
    (async () => {
      const hasSaved = Array.isArray(readJson(`${OS_CITIES_PREFIX}${shopId}`));
      try {
        if (!hasSaved) setLoadingFilterCities(true);
        const response = await apiFetch(`/order/api/get-cities-for-shop/?shop_id=${shopId}`);
        if (citiesRequestId !== citiesRequestRef.current) return;
        let citiesArr = [];
        if (response?.success && Array.isArray(response.cities)) {
          citiesArr = response.cities;
        } else if (response?.cities && Array.isArray(response.cities)) {
          citiesArr = response.cities;
        } else {
          const shop = shopsRef.current.find((s) => String(s.shop_id) === String(shopId));
          if (shop?.cities) citiesArr = shop.cities;
        }
        setShopCities(citiesArr);
        setFilterCities(citiesArr);
        if (citiesArr.length) writeJson(`${OS_CITIES_PREFIX}${shopId}`, citiesArr);
      } catch (error) {
        if (citiesRequestId !== citiesRequestRef.current) return;
        console.error('Error fetching cities:', error);
        const shop = shopsRef.current.find((s) => String(s.shop_id) === String(shopId));
        if (shop?.cities) {
          setShopCities(shop.cities);
          setFilterCities(shop.cities);
        }
      } finally {
        if (citiesRequestId === citiesRequestRef.current) setLoadingFilterCities(false);
      }
    })();

    // ----- all orders (Order Number dropdown + order ka live status) -----
    loadOrders(shopId);

    // ----- order status images -----
    const imagesRequestId = ++imagesRequestRef.current;
    (async () => {
      try {
        setError(null);
        const result = await apiFetch(`/order/order-status-images/?shop_id=${shopId}`);
        if (imagesRequestId !== imagesRequestRef.current) return;

        let imagesData = [];
        if (result && result.status === 'success' && Array.isArray(result.data)) {
          imagesData = result.data;
        } else if (result && Array.isArray(result.data)) {
          imagesData = result.data;
        } else if (Array.isArray(result)) {
          imagesData = result;
        }

        const transformedData = imagesData.map((item) => ({
          id: item.id,
          orderNumber: item.order ? `ORD-${item.order}` : 'N/A',
          status: item.status,
          image: item.image_url || item.image || '',
          dateUploaded: item.date_uploaded ? item.date_uploaded.split('T')[0] : '',
          timeUploaded: item.date_uploaded ? (item.date_uploaded.split('T')[1] || '').split('.')[0] : '',
          orderId: item.order_id || item.order || null,
          createdAt: item.date_uploaded || '',
          shop_id: item.shop?.shop_id || item.shop_id || shopId,
          shop_name: item.shop?.shop_name || item.shop_name || 'N/A',
          city: item.city || ''
        }));

        dataShopRef.current = shopId;
        setOrderstatusData(transformedData);
      } catch (err) {
        if (imagesRequestId !== imagesRequestRef.current) return;
        console.error('Error fetching order status images:', err);
        setError(err.message);
        if (dataShopRef.current !== shopId) setOrderstatusData([]);
      } finally {
        if (imagesRequestId === imagesRequestRef.current) setIsLoading(false);
      }
    })();
  }, [selectedShopId, apiFetch, loadOrders]);

  // ✅ Order ka status kisi aur page (Order Detail / Details tab) se badle to yahan bhi naya status aa jaye
  useEffect(() => {
    if (!selectedShopId) return undefined;
    const refresh = () => loadOrders(selectedShopId);
    window.addEventListener('orderUpdated', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('orderUpdated', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [selectedShopId, loadOrders]);

  // The saved copy of the list is kept up to date (also after add / edit / delete)
  useEffect(() => {
    if (!selectedShopId || dataShopRef.current !== selectedShopId) return;
    writeJson(`${OS_CACHE_PREFIX}${selectedShopId}`, orderstatusData);
  }, [orderstatusData, selectedShopId]);

  // ✅ City counts are calculated from the loaded records
  const cityCounts = useMemo(() => {
    const counts = {};
    orderstatusData.forEach((item) => {
      if (item.city) counts[item.city] = (counts[item.city] || 0) + 1;
    });
    return counts;
  }, [orderstatusData]);

  // ✅ Har order ka live status (order_Id -> status)
  const orderStatusMap = useMemo(() => {
    const map = {};
    allOrders.forEach((o) => {
      map[String(o.order_Id)] = normalizeOrderStatus(o.order_Status || o.status);
    });
    return map;
  }, [allOrders]);

  // ✅ Har order ka tracking ID (order_Id -> tracking id), taake order number hamesha ek jaisa dikhe
  const orderLabelMap = useMemo(() => {
    const map = {};
    allOrders.forEach((o) => {
      if (o.order_Tracking_Id) map[String(o.order_Id)] = o.order_Tracking_Id;
    });
    return map;
  }, [allOrders]);

  // Table ke Status column me order ka live status; order na mile to record ka apna status
  const getDisplayStatus = (item) => orderStatusMap[String(item.orderId)] || item.status || '';

  // Order number: tracking ID (na mile to ORD-id)
  const getOrderLabel = (item) => orderLabelMap[String(item.orderId)] || item.orderNumber || 'N/A';

  // ✅ Image record save hone par order ka status bhi wahi kar do
  const syncOrderStatus = async (order, imageStatus) => {
    const target = toOrderStatus(imageStatus);
    const current = normalizeOrderStatus(order.order_Status || order.status);
    if (!target || current === target) return { changed: false, failed: false };

    try {
      const payload = { order_Status: target };
      // total ko jyun ka tyun rakho (status badalne se amount dobara calculate na ho)
      if (order.total_Amount !== undefined && order.total_Amount !== null) {
        payload.total_Amount = order.total_Amount;
      }
      const updated = await apiFetch(`/order/update-order-details/${order.order_Id}/`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      const newStatus = normalizeOrderStatus(updated?.order_Status || target);

      const patch = (list) =>
        list.map((o) => (String(o.order_Id) === String(order.order_Id) ? { ...o, order_Status: newStatus } : o));
      setAllOrders(patch);
      setFilteredOrders(patch);

      // Orders list wagera ko batao ke order badal gaya
      window.dispatchEvent(new Event('orderUpdated'));
      return { changed: true, failed: false, status: newStatus };
    } catch (err) {
      console.error('Error syncing order status:', err);
      return { changed: false, failed: true };
    }
  };

  const showSavedAlert = (title, baseText, sync) => {
    if (sync && sync.failed) {
      return Swal.fire({
        icon: 'warning',
        title,
        text: `${baseText} But the order status could not be updated automatically. Please change it from the order page.`,
        confirmButtonColor: '#004aad'
      });
    }
    return Swal.fire({
      icon: 'success',
      title,
      text: sync && sync.changed ? `${baseText} Order status is now "${sync.status}".` : baseText,
      timer: sync && sync.changed ? 3000 : 2000,
      showConfirmButton: false
    });
  };

  const handleFilterCityChange = (e) => {
    setSelectedFilterCity(e.target.value);
    setPage(1); // ⭐ city filter change → page 1
  };

  // Filter by BOTH search term AND selected city
  const filteredData = useMemo(() => {
    let filtered = [...orderstatusData];

    if (selectedFilterCity && selectedFilterCity.trim() !== '') {
      filtered = filtered.filter((item) => item.city === selectedFilterCity);
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        (getOrderLabel(item) || '').toLowerCase().includes(search) ||
        (item.orderNumber || '').toLowerCase().includes(search) ||
        (getDisplayStatus(item) || '').toLowerCase().includes(search) ||
        (item.city || '').toLowerCase().includes(search)
      );
    }

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderstatusData, searchTerm, selectedFilterCity, orderStatusMap, orderLabelMap]);

  // ===== ⭐ Pagination (exactly like Addresss) =====
  const pages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const current = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ⭐ Filter / search / delete se pages kam ho jayein to page number bahar na reh jaye
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  // ⭐ Header checkbox state (current page only)
  const allPageSelected =
    current.length > 0 && current.every((item) => selectedItems.includes(item.id));

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // ⭐ search change → page 1
  };

  // ⭐ Select All → only current page rows
  const handleSelectAll = () => {
    if (current.length === 0) return;
    if (allPageSelected) {
      setSelectedItems(selectedItems.filter((id) => !current.some((item) => item.id === id)));
    } else {
      const newSel = [...selectedItems];
      current.forEach((item) => {
        if (!newSel.includes(item.id)) newSel.push(item.id);
      });
      setSelectedItems(newSel);
    }
    setShowActionDropdown(false);
  };

  const handleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) setSelectedItems(selectedItems.filter((id) => id !== itemId));
    else setSelectedItems([...selectedItems, itemId]);
    setShowActionDropdown(false);
  };

  const deleteOrderStatusImage = async (id) => {
    try {
      await apiFetch(`/order/order-status-images/${id}/?shop_id=${selectedShopId}`, { method: 'DELETE' });
      setOrderstatusData((prev) => prev.filter((item) => item.id !== id));
      setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));
      return true;
    } catch (err) { throw err; }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      await Swal.fire({ icon: 'warning', title: 'No Selection', text: 'Please select items to delete', confirmButtonColor: '#004aad' });
      return;
    }
    const selectedCount = selectedItems.length;
    const result = await Swal.fire({
      title: 'Delete Selected Records?',
      text: `Are you sure you want to delete ${selectedCount} record(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });
    if (result.isConfirmed) {
      setLoading(true);
      try {
        for (const id of [...selectedItems]) await deleteOrderStatusImage(id);
        setSelectedItems([]);
        setShowActionDropdown(false);
        window.dispatchEvent(new Event('orderStatusImageUpdated'));
        await Swal.fire({ icon: 'success', title: 'Deleted!', text: `${selectedCount} record(s) deleted successfully!`, timer: 2000, showConfirmButton: false });
      } catch (err) {
        await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete some items. Please try again.', confirmButtonColor: '#004aad' });
      } finally { setLoading(false); }
    }
  };

  const handleClearAll = () => { setSelectedItems([]); setShowActionDropdown(false); };

  const handleAddNew = () => {
    setEditingId(null);
    setViewMode(false);
    setViewItem(null);
    setSelectedOrderId('');
    setStatus('');
    setImagePreview('');
    setImageFile(null);
    setDateUploaded('');
    setTimeUploaded('');
    setSelectedCity('');
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setViewMode(false);
    setViewItem(item);
    setSelectedOrderId(getOrderLabel(item));
    setStatus(getDisplayStatus(item) === 'Pending' ? 'Order Placed' : (getDisplayStatus(item) === 'Canceled' ? 'Cancelled' : getDisplayStatus(item)));
    setImagePreview(item.image || '');
    setDateUploaded(item.dateUploaded || '');
    setTimeUploaded(item.timeUploaded || '');
    setSelectedCity(item.city || '');
    setImageFile(null);
    setIsModalOpen(true);
  };

  const handleViewDetails = (item) => {
    setViewMode(true);
    setViewItem(item);
    setSelectedOrderId(getOrderLabel(item));
    setStatus(getDisplayStatus(item));
    setImagePreview(item.image || '');
    setDateUploaded(item.dateUploaded || '');
    setTimeUploaded(item.timeUploaded || '');
    setSelectedCity(item.city || '');
    setIsModalOpen(true);
  };

  const handleDelete = async (itemId) => {
    const result = await Swal.fire({
      title: 'Delete Record?',
      text: 'Are you sure you want to delete this record?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });
    if (result.isConfirmed) {
      try {
        await deleteOrderStatusImage(itemId);
        window.dispatchEvent(new Event('orderStatusImageUpdated'));
        await Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Record deleted successfully!', timer: 1500, showConfirmButton: false });
      } catch (err) {
        await Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to delete record.', confirmButtonColor: '#004aad' });
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!selectedOrderId.trim()) {
      await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please select Order Number', confirmButtonColor: '#004aad' });
      return;
    }
    if (!status) {
      await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please select status', confirmButtonColor: '#004aad' });
      return;
    }
    if (!imagePreview && editingId === null) {
      await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please select an image', confirmButtonColor: '#004aad' });
      return;
    }
    setLoading(true);
    try {
      if (editingId !== null) await updateOrderStatusImage(editingId);
      else await createOrderStatusImage();
    } catch (err) {
      await Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to save record. Please try again.', confirmButtonColor: '#004aad' });
    } finally {
      setLoading(false);
    }
  };

  const createOrderStatusImage = async () => {
    try {
      const selectedOrder = filteredOrders.find(
        (order) => order.order_Tracking_Id === selectedOrderId || `ORD-${order.order_Id}` === selectedOrderId
      );
      if (!selectedOrder) throw new Error('Selected order not found');

      const formData = new FormData();
      if (imageFile) formData.append('image', imageFile);
      else if (imagePreview) {
        const response = await fetch(imagePreview);
        const blob = await response.blob();
        const file = new File([blob], 'image.jpg', { type: blob.type || 'image/jpeg' });
        formData.append('image', file);
      } else throw new Error('Please select an image');

      formData.append('shop_id', selectedShopId);
      formData.append('order', selectedOrder.order_Id);
      formData.append('status', status);
      formData.append('city', selectedCity || '');
      if (dateUploaded) {
        const dateTime = timeUploaded ? `${dateUploaded}T${timeUploaded}` : `${dateUploaded}T00:00:00`;
        formData.append('date_uploaded', dateTime);
      }

      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/order/order-status-images/create/`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });

      const contentType = response.headers.get('content-type');
      let result;
      if (contentType && contentType.includes('application/json')) result = await response.json();
      else {
        const textResponse = await response.text();
        throw new Error(`Server returned non-JSON response: ${textResponse.substring(0, 200)}`);
      }

      if (!response.ok) throw new Error(result?.error || result?.message || result?.detail || `Failed to create record: ${response.status}`);

      const newItem = {
        id: result.id,
        orderNumber: `ORD-${selectedOrder.order_Id}`,
        status,
        image: result.image_url || result.image || imagePreview,
        dateUploaded,
        timeUploaded,
        orderId: result.order_id || selectedOrder.order_Id,
        createdAt: result.date_uploaded || '',
        shop_id: parseInt(selectedShopId, 10),
        shop_name: shops.find((s) => String(s.shop_id) === String(selectedShopId))?.shop_name || 'N/A',
        city: selectedCity || ''
      };

      setOrderstatusData((prevData) => [...prevData, newItem]);

      // ✅ Order ka status bhi isi status par kar do
      const sync = await syncOrderStatus(selectedOrder, status);
      window.dispatchEvent(new Event('orderStatusImageUpdated'));

      setIsModalOpen(false);
      resetForm();
      await showSavedAlert('Added!', 'Record added successfully!', sync);
    } catch (err) { throw err; }
  };

  const updateOrderStatusImage = async (id) => {
    try {
      const selectedOrder = filteredOrders.find(
        (order) => order.order_Tracking_Id === selectedOrderId || `ORD-${order.order_Id}` === selectedOrderId
      );
      if (!selectedOrder) throw new Error('Selected order not found');

      let imageUrl = imagePreview;
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        const token = getAuthToken();
        const uploadResponse = await fetch(`${API_BASE_URL}/order/order-status-images/upload/`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: imageFormData
        });
        if (!uploadResponse.ok) throw new Error('Failed to upload image');
        const uploadResult = await uploadResponse.json();
        imageUrl = uploadResult.image_url || uploadResult.image || imagePreview;
      }

      const putData = {
        status,
        order: selectedOrder.order_Id,
        shop_id: parseInt(selectedShopId, 10),
        image: imageUrl,
        city: selectedCity || ''
      };
      if (dateUploaded) putData.date_uploaded = timeUploaded ? `${dateUploaded}T${timeUploaded}` : `${dateUploaded}T00:00:00`;

      const result = await apiFetch(`/order/order-status-images/${id}/`, { method: 'PUT', body: JSON.stringify(putData) });

      setOrderstatusData((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                orderNumber: `ORD-${selectedOrder.order_Id}`,
                orderId: selectedOrder.order_Id,
                status,
                image: result?.image_url || result?.image || imageUrl,
                dateUploaded,
                timeUploaded,
                city: selectedCity || ''
              }
            : item
        )
      );

      // ✅ Order ka status bhi isi status par kar do
      const sync = await syncOrderStatus(selectedOrder, status);
      window.dispatchEvent(new Event('orderStatusImageUpdated'));

      setIsModalOpen(false);
      resetForm();
      await showSavedAlert('Updated!', 'Record updated successfully!', sync);
    } catch (err) { throw err; }
  };

  const resetForm = () => {
    setSelectedOrderId('');
    setStatus('');
    setImagePreview('');
    setImageFile(null);
    setDateUploaded('');
    setTimeUploaded('');
    setSelectedCity('');
    setEditingId(null);
    setViewMode(false);
    setViewItem(null);
  };

  const handleCloseModal = () => { setIsModalOpen(false); resetForm(); };

  const handleClickOutside = useCallback((e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) setShowActionDropdown(false);
  }, [showActionDropdown]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => { document.removeEventListener('click', handleClickOutside); };
  }, [handleClickOutside]);

  // Error screen
  if (error && orderstatusData.length === 0 && !isLoading) {
    return (
      <div className="orderstatus-main-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center', color: '#dc3545' }}>
          <FaImage style={{ fontSize: '3rem' }} />
          <p style={{ marginTop: '1rem' }}>{error}</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', background: '#004aad', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="orderstatus-main-container">
      {/* ✅ HEADER */}
      <div className="orderstatus-header-section">
        <div className="orderstatus-header-left">
          <FaFileImage className="orderstatus-header-icon" />
          <h2 className="orderstatus-header-title">Order Status Images</h2>
          {/* ✅ City Badge — ALWAYS shown; "All Cities" when no city selected */}
          <span className="shop-city-badge">
            <FaMapMarkerAlt className="shop-city-icon" />
            <span className="shop-city-name">
              {selectedFilterCity
                ? `${selectedFilterCity} (${cityCounts[selectedFilterCity] || 0})`
                : `All Cities (${filteredData.length})`}
            </span>
          </span>
        </div>

        <div className="orderstatus-header-right">
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={loadingFilterCities || filterCities.length === 0}
            >
              {/* ✅ Default option = All Cities */}
              <option value="">
                {loadingFilterCities ? 'Loading...' : 'All Cities'}
              </option>
              {filterCities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({cityCounts[city] || 0})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by Order Number, Status, or City..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>

          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>{allPageSelected ? 'Deselect All' : 'Select All'}</span>
          </button>

          <button className="add-item-btn" onClick={handleAddNew}>
            <FaPlus />
            <span>ADD NEW IMAGE</span>
          </button>
        </div>
      </div>

      <div className="orderstatus-content">
        {selectedItems.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">{selectedItems.length} item(s) selected</span>
              <div className="action-dropdown-container">
                <button className="action-dropdown-btn" onClick={() => setShowActionDropdown(!showActionDropdown)}>
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button className="dropdown-item delete-item" onClick={handleDeleteSelected}>
                      <FaTrash />
                      <span>Delete Selected ({selectedItems.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="clear-selection" onClick={handleClearAll}>Clear all</button>
            </div>
          </div>
        )}

        <div className="orderstatus-table-container">
          <div className="orderstatus-table-wrapper">
            {error ? (
              <div className="error-state">
                <p>Error: {error}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
              </div>
            ) : (
              <table className="orderstatus-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <input type="checkbox" checked={allPageSelected} onChange={handleSelectAll} />
                    </th>
                    <th>Order Number</th>
                    <th>Status</th>
                    <th>City</th>
                    <th>Image</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Nothing is shown until the first load is done, so "No order status records found" never flashes */}
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" style={{ height: '200px' }}></td>
                    </tr>
                  ) : current.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="no-data-cell">
                        <FaBuilding className="no-data-icon" />
                        <p>
                          {selectedFilterCity
                            ? `No order status records found in "${selectedFilterCity}".`
                            : `No order status records found for ${currentShopName}.`}
                        </p>
                        <button className="add-first-btn" onClick={handleAddNew}>
                          <FaPlus /> Add Your First Record
                        </button>
                      </td>
                    </tr>
                  ) : (
                    current.map((item) => {
                      const displayStatus = getDisplayStatus(item);
                      return (
                        <tr key={item.id} className={selectedItems.includes(item.id) ? 'selected' : ''}>
                          <td>
                            <input type="checkbox" checked={selectedItems.includes(item.id)} onChange={() => handleSelectItem(item.id)} />
                          </td>
                          <td className="order-cell">{getOrderLabel(item)}</td>
                          {/* ✅ Sirf ek Status column: order ka live status */}
                          <td className="status-cell">
                            <span className={`status-badge status-${(displayStatus || '').toLowerCase().replace(/\s+/g, '-')}`}>
                              {displayStatus}
                            </span>
                          </td>
                          <td className="city-cell">
                            {item.city || '-'}
                          </td>
                          <td className="image-cell">
                            {item.image ? (
                              <img src={item.image} alt={getOrderLabel(item)} className="order-image" onClick={() => handleViewDetails(item)} />
                            ) : (
                              <FaFileImage className="no-image-icon" />
                            )}
                          </td>
                          <td className="actions-cell">
                            <div className="actions-cell-inner">
                              <button className="edit-item-btn" onClick={() => handleEdit(item)} title="Edit Item">
                                <FaEdit />
                              </button>
                              <button className="delete-item-btn" onClick={() => handleDelete(item.id)} title="Delete Item">
                                <RiDeleteBinFill />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ✅ 10 per page: 1-10 of N | < Previous | Page 1 of N | Next > */}
        <TablePagination
          page={page}
          totalItems={filteredData.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      {/* Modal - 2 FIELDS PER ROW */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{viewMode ? 'Order Status Details' : editingId !== null ? 'Edit Order Status' : 'Add New Order Status'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <form className="orderstatus-form">
                <div className="form-group" style={{ display: 'none' }}>
                  <label>Select Shop *</label>
                  <select value={selectedShopId} onChange={(e) => { setSelectedShopId(e.target.value); setSelectedOrderId(''); }} className="form-select" required>
                    {shops.map((shop) => (<option key={shop.shop_id} value={shop.shop_id}>{shop.shop_name}</option>))}
                  </select>
                </div>

                {/* ✅ 2 FIELDS IN ONE LINE (City + Status) */}
                <div className="form-row-modal">
                  <div className="form-group">
                    <label>City:</label>
                    {viewMode ? (
                      <div className="view-field">{selectedCity || <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>}</div>
                    ) : (
                      <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} className="form-input">
                        <option value="">Select City</option>
                        {shopCities.map((city, index) => (<option key={index} value={city}>{city}</option>))}
                      </select>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Status:</label>
                    {viewMode ? (
                      <div className="view-field">
                        <span className={`status-badge status-${(status || '').toLowerCase().replace(/\s+/g, '-')}`}>{status}</span>
                      </div>
                    ) : (
                      <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input" required>
                        <option value="">Select status</option>
                        <option value="Order Placed">Order Placed</option>
                        <option value="Processing">Processing</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Returned">Returned</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    )}
                  </div>
                </div>

                {/* ✅ Order Number - Full width (kyunke isme customer name bhi ata hai) */}
                <div className="form-group">
                  <label>Order Number *</label>
                  {viewMode ? (
                    <div className="view-field"><strong>{selectedOrderId}</strong></div>
                  ) : (
                    <select value={selectedOrderId} onChange={(e) => setSelectedOrderId(e.target.value)} className="form-select" required>
                      <option value="">Select Order</option>
                      {filteredOrders.length > 0 ? (
                        filteredOrders.map((order) => (
                          <option key={order.order_Id} value={order.order_Tracking_Id || `ORD-${order.order_Id}`}>
                            {order.order_Tracking_Id || `ORD-${order.order_Id}`} - {order.customer?.first_Name || 'Unknown'} {order.customer?.last_Name || ''}
                          </option>
                        ))
                      ) : (<option value="" disabled>No orders found for selected shop</option>)}
                    </select>
                  )}
                </div>

                {/* ✅ Image - Full width */}
                <div className="form-group">
                  <label>Image:</label>
                  {viewMode ? (
                    <div className="image-view-container">
                      {imagePreview && <img src={imagePreview} alt="Order" className="modal-image" />}
                    </div>
                  ) : (
                    <div className="image-upload-container">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="image-input"
                        id="image-upload"
                      />

                      <label
                        htmlFor="image-upload"
                        className="image-upload-label"
                      >
                        Choose File
                      </label>

                      <span className="file-name">
                        {imageFile
                          ? imageFile.name
                          : imagePreview
                            ? 'File selected'
                            : 'No file chosen'}
                      </span>

                      {imagePreview && (
                        <div className="image-preview-modal">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="preview-image-modal"
                          />

                          <button
                            type="button"
                            className="remove-image-modal"
                            onClick={() => {
                              setImagePreview('');
                              setImageFile(null);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ✅ 2 FIELDS IN ONE LINE (Date + Time) */}
                <div className="form-row-modal">
                  <div className="form-group">
                    <label>Date:</label>
                    {viewMode ? (<div className="view-field">{dateUploaded}</div>) : (
                      <input type="date" value={dateUploaded} onChange={(e) => setDateUploaded(e.target.value)} className="form-input" />
                    )}
                  </div>
                  <div className="form-group">
                    <label>Time:</label>
                    {viewMode ? (<div className="view-field">{timeUploaded}</div>) : (
                      <input type="time" value={timeUploaded} onChange={(e) => setTimeUploaded(e.target.value)} className="form-input" />
                    )}
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseModal}>Close</button>
              {!viewMode && (
                <button className="btn-save" onClick={handleSave} disabled={loading}>
                  {loading ? (<FaSpinner style={{ animation: 'spin 1s linear infinite' }} />) : editingId !== null ? 'Update' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderstatusImage;